type YoutubeDigestJobStatus =
  | "queued"
  | "probing"
  | "fetching"
  | "generating"
  | "coverage"
  | "saving"
  | "awaiting_overwrite"
  | "done"
  | "done_with_warning"
  | "failed"
  | "cancelling"
  | "cancelled";

type YoutubeDigestDurableState =
  | "checking"
  | "not_extracted"
  | "extracted"
  | "check_failed";

type YoutubeDigestJobView = {
  id: string;
  sessionId: string;
  roomId?: string | null;
  ownerActorId?: string | null;
  sourceUrl: string;
  videoId: string;
  status: YoutubeDigestJobStatus;
  createdAt?: string;
  updatedAt?: string;
  warningLabels?: string[];
  error?: string | null;
  cancelRequestedAt?: string | null;
  notePath?: string | null;
  artifactId?: string | null;
};

type YoutubeDigestWsPayload = {
  sessionId?: string;
  jobId?: string;
  job?: YoutubeDigestJobView;
};

type YoutubeDigestCardDecorateOptions = {
  sessionId?: string;
};

interface YoutubeDigestCardBridge {
  decorateMessage(message: HTMLElement | null, text: string, options?: YoutubeDigestCardDecorateOptions): boolean;
  onJobUpdate(data: YoutubeDigestWsPayload): void;
  restoreForSession(sessionId: string): Promise<void>;
}

interface Window {
  YoutubeDigestCard?: YoutubeDigestCardBridge;
  currentSessionId?: string | null;
}

type FetchResponseLike = Pick<Response, "ok" | "status" | "json">;

var YoutubeDigestCard = (function () {
  const URL_RE = /https?:\/\/[^\s<>"']+/ig;
  const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
  const ACTIVE_STATUSES = new Set(["queued", "probing", "fetching", "generating", "coverage", "saving", "cancelling"]);
  const CARD_SELECTOR = ".youtube-digest-card";
  // 세션별로 마지막에 본 job 스냅샷. 원본 메시지가 lazy-load 범위 밖이라 restore 시점에 카드가 없던
  // 진행 중 job을, 사용자가 위로 스크롤해 원본이 렌더될 때 그 카드가 이어받게 한다.
  const knownJobsBySession = new Map<string, Map<string, YoutubeDigestJobView>>();
  const STATUS_LABELS: Record<string, string> = {
    idle: "준비됨",
    checking: "확인 중",
    not_extracted: "미추출",
    extracted: "추출됨",
    check_failed: "확인 실패",
    queued: "대기 중",
    probing: "영상 확인 중",
    fetching: "자막 수집 중",
    generating: "digest 생성 중",
    coverage: "검증 중",
    saving: "저장 중",
    awaiting_overwrite: "덮어쓰기 확인",
    done: "완료",
    done_with_warning: "경고 있음",
    failed: "실패",
    cancelling: "취소 중",
    cancelled: "취소됨",
    disconnected: "연결 끊김",
    job_lost: "상태 유실",
  };
  const STATUS_COPY: Record<string, string> = {
    idle: "YouTube 영상 URL을 감지했어요. 자막 digest를 시작할 수 있어요.",
    checking: "기존 digest가 있는지 확인하고 있어요.",
    not_extracted: "아직 Inbox에 digest가 없어요. 지금 자막 digest를 시작할 수 있어요.",
    extracted: "이미 Inbox에 digest가 있어요. 원하면 최신 내용으로 재추출할 수 있어요.",
    check_failed: "기존 digest 상태를 확인하지 못했어요. 상태를 다시 확인해 주세요.",
    queued: "작업을 큐에 넣었어요. 곧 영상을 확인합니다.",
    probing: "영상 메타데이터를 확인하고 있어요.",
    fetching: "자막을 수집하고 있어요.",
    generating: "자막을 digest로 정리하고 있어요.",
    coverage: "누락과 수치 커버리지를 점검하고 있어요.",
    saving: "Inbox 노트와 채팅 artifact를 저장하고 있어요.",
    awaiting_overwrite: "기존 Inbox 노트를 덮어쓰기 전에 확인이 필요해요.",
    done: "Inbox 노트와 채팅 artifact 저장을 마쳤어요.",
    done_with_warning: "저장은 마쳤지만 coverage 경고가 남았어요.",
    failed: "작업이 실패했어요.",
    cancelling: "작업 취소를 요청했어요.",
    cancelled: "작업을 취소했어요.",
    disconnected: "연결이 끊겨 현재 상태를 확인하지 못했어요.",
    job_lost: "작업 상태를 찾지 못했어요. 다시 시작해야 해요.",
  };
  const WARNING_LABELS: Record<string, string> = {
    missing_headings: "헤딩 누락",
    section_coverage_below_threshold: "구간 커버리지 부족",
    numeric_fact_coverage_below_threshold: "수치 커버리지 부족",
  };
  const ERROR_COPY: Record<string, string> = {
    invalid_youtube_url: "유효한 YouTube 영상 URL만 시작할 수 있어요.",
    trust_boundary_unavailable: "현재 digest 실행 권한이 닫혀 있어요.",
    yt_dlp_unavailable: "yt-dlp를 사용할 수 없어 자막을 가져올 수 없어요.",
    YT_DLP_FAILED: "YouTube가 자막 요청을 제한했거나 자막 다운로드에 실패했어요. 잠시 후 다시 시도해 주세요.",
    YOUTUBE_SUBTITLE_UNAVAILABLE: "요청한 언어의 자막을 찾지 못했어요.",
    youtube_digest_video_locked: "같은 영상에 대해 다른 digest가 이미 진행 중이에요.",
    youtube_digest_actor_limit: "현재 계정은 digest를 하나만 동시에 실행할 수 있어요.",
    youtube_digest_global_limit: "전체 digest 슬롯이 꽉 차 잠시 후 다시 시도해야 해요.",
    youtube_digest_runtime_unavailable: "digest 런타임이 아직 준비되지 않았어요.",
    youtube_digest_overwrite_required: "기존 노트를 덮어쓰기 전에 확인이 필요해요.",
    youtube_digest_reextract_confirmation_required: "기존 digest 상태가 바뀌어 다시 확인이 필요해요.",
    youtube_digest_overwrite_not_pending: "지금은 덮어쓰기를 확인할 수 없는 상태예요.",
    youtube_digest_job_cancelled: "작업이 취소되었어요.",
    youtube_digest_artifact_skipped_after_cancel: "노트는 저장됐지만 취소로 artifact 저장은 건너뛰었어요.",
    youtube_digest_job_gone: "작업 상태가 사라져 다시 시작해야 해요.",
    session_not_found: "세션을 찾지 못해 상태를 확인할 수 없어요.",
    forbidden: "이 세션의 digest 상태를 볼 수 없어요.",
    not_authenticated: "로그인이 필요해요.",
  };

  function trimUrl(raw: string): string {
    return String(raw || "").replace(/[),.!?]+$/, "");
  }

  function parseYoutubeVideoId(urlText: string): string | null {
    let parsed: URL;
    try {
      parsed = new URL(urlText);
    } catch {
      return null;
    }
    const host = parsed.hostname.toLowerCase();
    if (host === "youtu.be") {
      const candidate = parsed.pathname.replace(/^\/+/, "").split("/")[0] || "";
      return YOUTUBE_ID_RE.test(candidate) ? candidate : null;
    }
    if (!["youtube.com", "www.youtube.com", "m.youtube.com", "music.youtube.com"].includes(host)) return null;
    const watchId = parsed.searchParams.get("v");
    if (watchId && YOUTUBE_ID_RE.test(watchId)) return watchId;
    for (const prefix of ["/embed/", "/shorts/", "/live/", "/v/"]) {
      if (parsed.pathname.startsWith(prefix)) {
        const candidate = parsed.pathname.slice(prefix.length).split("/")[0] || "";
        if (YOUTUBE_ID_RE.test(candidate)) return candidate;
      }
    }
    return null;
  }

  function firstYoutubeUrl(text: string): string | null {
    const matches = String(text || "").match(URL_RE) || [];
    for (const match of matches) {
      const url = trimUrl(match);
      if (parseYoutubeVideoId(url)) return url;
    }
    return null;
  }

  function currentSessionId(): string | null {
    return typeof window.currentSessionId === "string" && window.currentSessionId
      ? window.currentSessionId
      : null;
  }

  function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  function isJobView(value: unknown): value is YoutubeDigestJobView {
    return isRecord(value)
      && typeof value.id === "string"
      && typeof value.sessionId === "string"
      && typeof value.sourceUrl === "string"
      && typeof value.videoId === "string"
      && typeof value.status === "string";
  }

  function rememberJob(job: YoutubeDigestJobView): void {
    let sessionJobs = knownJobsBySession.get(job.sessionId);
    if (!sessionJobs) {
      sessionJobs = new Map();
      knownJobsBySession.set(job.sessionId, sessionJobs);
    }
    sessionJobs.set(job.id, job);
  }

  function replaceSessionJobs(sessionId: string, jobs: YoutubeDigestJobView[]): void {
    knownJobsBySession.set(sessionId, new Map(jobs.map((job) => [job.id, job])));
  }

  function isPendingJobStatus(status: YoutubeDigestJobStatus): boolean {
    return status === "awaiting_overwrite" || ACTIVE_STATUSES.has(status);
  }

  function pendingJobForCard(sessionId: string, sourceUrl: string): YoutubeDigestJobView | null {
    const sessionJobs = knownJobsBySession.get(sessionId);
    if (!sessionJobs) return null;
    let latest: YoutubeDigestJobView | null = null;
    for (const job of sessionJobs.values()) {
      if (job.sourceUrl !== sourceUrl || !isPendingJobStatus(job.status)) continue;
      if (!latest || String(job.createdAt || "") > String(latest.createdAt || "")) latest = job;
    }
    return latest;
  }

  function resolveContentHost(message: HTMLElement | null): HTMLElement | null {
    return message?.querySelector<HTMLElement>(".message-content") || null;
  }

  function escapeAttr(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
  }

  function jobState(card: HTMLElement): YoutubeDigestJobStatus | null {
    const state = card.getAttribute("data-youtube-job-status");
    return typeof state === "string" && state ? state as YoutubeDigestJobStatus : null;
  }

  function durableState(card: HTMLElement): YoutubeDigestDurableState {
    const state = card.getAttribute("data-youtube-durable-state");
    if (state === "checking" || state === "not_extracted" || state === "extracted" || state === "check_failed") {
      return state;
    }
    return "checking";
  }

  function durableRevision(card: HTMLElement): string | null {
    const revision = card.getAttribute("data-youtube-durable-revision");
    return revision ? revision : null;
  }

  function durableCopyOverride(card: HTMLElement): string | null {
    const copy = card.getAttribute("data-youtube-durable-copy-override");
    return copy ? copy : null;
  }

  function jobWarningCopy(card: HTMLElement): string {
    return card.getAttribute("data-youtube-warning-copy") || "";
  }

  function jobNotePath(card: HTMLElement): string {
    return card.getAttribute("data-youtube-note-path") || "";
  }

  function jobUpdatedAt(card: HTMLElement): string {
    return card.getAttribute("data-youtube-updated-at") || "";
  }

  function jobErrorCode(card: HTMLElement): string {
    return card.getAttribute("data-youtube-job-error") || "";
  }

  function urlLine(card: HTMLElement): HTMLElement | null {
    return card.querySelector<HTMLElement>(".youtube-digest-card-url");
  }

  function statusLine(card: HTMLElement): HTMLElement | null {
    return card.querySelector<HTMLElement>(".youtube-digest-card-status");
  }

  function metaLine(card: HTMLElement): HTMLElement | null {
    return card.querySelector<HTMLElement>(".youtube-digest-card-meta");
  }

  function badgeLine(card: HTMLElement): HTMLElement | null {
    return card.querySelector<HTMLElement>(".youtube-digest-card-badge");
  }

  function actionsLine(card: HTMLElement): HTMLElement | null {
    return card.querySelector<HTMLElement>(".youtube-digest-card-actions");
  }

  function createAction(label: string, kind: string, accent = false): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `ui-btn youtube-digest-card-btn${accent ? " youtube-digest-card-btn-primary" : ""}`;
    button.setAttribute("data-youtube-action", kind);
    button.textContent = label;
    return button;
  }

  function setCardState(card: HTMLElement, state: string, copy: string): void {
    card.setAttribute("data-youtube-state", state);
    const badge = badgeLine(card);
    if (badge) {
      badge.textContent = STATUS_LABELS[state] || STATUS_LABELS.failed;
      badge.setAttribute("data-state", state);
    }
    const status = statusLine(card);
    if (status) status.textContent = copy;
  }

  function warningCopy(warningLabels: unknown): string {
    if (!Array.isArray(warningLabels) || warningLabels.length === 0) return "";
    const labels = warningLabels
      .filter((label): label is string => typeof label === "string")
      .map((label) => WARNING_LABELS[label] || label)
      .join(" · ");
    return labels ? `경고: ${labels}` : "";
  }

  function renderMeta(card: HTMLElement, job?: YoutubeDigestJobView | null): void {
    const meta = metaLine(card);
    if (!meta) return;
    const lines: string[] = [];
    const warning = job ? warningCopy(job.warningLabels) : jobWarningCopy(card);
    if (warning) lines.push(warning);
    const notePath = job?.notePath || jobNotePath(card);
    if (notePath) lines.push(`노트: ${notePath}`);
    const updatedAt = job?.updatedAt || jobUpdatedAt(card);
    if (updatedAt) lines.push(`갱신: ${updatedAt}`);
    meta.textContent = lines.join(" · ");
    meta.hidden = lines.length === 0;
  }

  function setBusy(card: HTMLElement, busy: boolean): void {
    card.setAttribute("data-youtube-busy", busy ? "true" : "false");
    actionsLine(card)?.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
      button.disabled = busy;
    });
  }

  function setDurableState(card: HTMLElement, state: YoutubeDigestDurableState, revision: string | null, copyOverride?: string | null): void {
    card.setAttribute("data-youtube-durable-state", state);
    if (revision) card.setAttribute("data-youtube-durable-revision", revision);
    else card.removeAttribute("data-youtube-durable-revision");
    if (copyOverride) card.setAttribute("data-youtube-durable-copy-override", copyOverride);
    else card.removeAttribute("data-youtube-durable-copy-override");
  }

  function persistJobSnapshot(card: HTMLElement, job: YoutubeDigestJobView): void {
    card.setAttribute("data-youtube-job-id", job.id);
    card.setAttribute("data-session-id", job.sessionId);
    card.setAttribute("data-youtube-source-url", job.sourceUrl);
    card.setAttribute("data-youtube-job-status", job.status);
    if (job.error) card.setAttribute("data-youtube-job-error", job.error);
    else card.removeAttribute("data-youtube-job-error");
    const warning = warningCopy(job.warningLabels);
    if (warning) card.setAttribute("data-youtube-warning-copy", warning);
    else card.removeAttribute("data-youtube-warning-copy");
    if (job.notePath) card.setAttribute("data-youtube-note-path", job.notePath);
    else card.removeAttribute("data-youtube-note-path");
    if (job.updatedAt) card.setAttribute("data-youtube-updated-at", job.updatedAt);
    else card.removeAttribute("data-youtube-updated-at");
  }

  function clearTransientJobSnapshot(card: HTMLElement): void {
    card.removeAttribute("data-youtube-job-id");
    card.removeAttribute("data-youtube-job-status");
    card.removeAttribute("data-youtube-job-error");
    card.removeAttribute("data-youtube-warning-copy");
    card.removeAttribute("data-youtube-note-path");
    card.removeAttribute("data-youtube-updated-at");
  }

  function effectiveCardState(card: HTMLElement): string {
    const transient = jobState(card);
    if (transient === "awaiting_overwrite") return transient;
    if (transient && ACTIVE_STATUSES.has(transient)) return transient;
    return durableState(card);
  }

  function effectiveCopy(card: HTMLElement): string {
    const transient = jobState(card);
    if (transient === "awaiting_overwrite" || (transient && ACTIVE_STATUSES.has(transient))) {
      const errorCode = jobErrorCode(card);
      return errorCode && ERROR_COPY[errorCode]
        ? ERROR_COPY[errorCode]
        : (transient ? STATUS_COPY[transient] : STATUS_COPY.failed) || STATUS_COPY.failed;
    }
    return durableCopyOverride(card) || STATUS_COPY[durableState(card)] || STATUS_COPY.failed;
  }

  function renderActions(card: HTMLElement): void {
    const actions = actionsLine(card);
    if (!actions) return;
    actions.innerHTML = "";
    const transient = jobState(card);
    const durable = durableState(card);
    const pendingAction = card.getAttribute("data-youtube-pending-action");
    if (pendingAction) {
      const pending = createAction(pendingAction, "pending", true);
      pending.disabled = true;
      actions.appendChild(pending);
      return;
    }
    if (transient === "awaiting_overwrite") {
      actions.append(createAction("취소", "cancel"), createAction("덮어쓰기", "overwrite", true));
      return;
    }
    if (transient && ACTIVE_STATUSES.has(transient)) {
      actions.appendChild(createAction("취소", "cancel"));
      return;
    }
    if (durable === "check_failed") {
      actions.appendChild(createAction("상태 다시 확인", "refresh", true));
      return;
    }
    if (durable === "checking") {
      const pending = createAction("확인 중...", "pending", true);
      pending.disabled = true;
      actions.appendChild(pending);
      return;
    }
    if (durable === "extracted") {
      actions.appendChild(createAction("재추출", "reextract", true));
      return;
    }
    actions.appendChild(createAction("digest 시작", "start", true));
  }

  function syncCardPresentation(card: HTMLElement, job?: YoutubeDigestJobView | null): void {
    const line = urlLine(card);
    const sourceUrl = card.getAttribute("data-youtube-source-url") || "";
    if (line) line.textContent = sourceUrl;
    setCardState(card, effectiveCardState(card), effectiveCopy(card));
    renderMeta(card, job);
    card.removeAttribute("data-youtube-pending-action");
    renderActions(card);
    setBusy(card, false);
  }

  function resetCardToState(card: HTMLElement, state: string, copy: string): void {
    clearTransientJobSnapshot(card);
    card.removeAttribute("data-youtube-pending-action");
    setCardState(card, state, copy);
    renderMeta(card, null);
    renderActions(card);
  }

  function buildCard(url: string, sessionId: string | null): HTMLElement {
    const card = document.createElement("div");
    card.className = "youtube-digest-card";
    card.setAttribute("data-youtube-source-url", url);
    if (sessionId) card.setAttribute("data-session-id", sessionId);
    card.innerHTML = `
      <div class="youtube-digest-card-head">
        <span class="youtube-digest-card-kicker">YouTube 자막 digest</span>
        <span class="youtube-digest-card-badge" data-state="idle">준비됨</span>
      </div>
      <div class="youtube-digest-card-url"></div>
      <div class="youtube-digest-card-status" role="status" aria-live="polite"></div>
      <div class="youtube-digest-card-meta" hidden></div>
      <div class="youtube-digest-card-actions"></div>
    `;
    const line = urlLine(card);
    if (line) line.textContent = url;
    setDurableState(card, "checking", null);
    setCardState(card, "checking", STATUS_COPY.checking);
    renderMeta(card, null);
    renderActions(card);
    wireActions(card);
    return card;
  }

  function wireActions(card: HTMLElement): void {
    card.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const action = target.closest<HTMLElement>("[data-youtube-action]")?.getAttribute("data-youtube-action");
      if (!action) return;
      if (action === "start") {
        void startJob(card);
        return;
      }
      if (action === "reextract") {
        confirmReextract(card);
        return;
      }
      if (action === "cancel") {
        void cancelJob(card);
        return;
      }
      if (action === "overwrite") {
        confirmOverwrite(card);
        return;
      }
      if (action === "refresh") {
        void refreshDigestStatus(card);
      }
    });
  }

  function matchCardForJob(job: YoutubeDigestJobView): HTMLElement | null {
    const byJobId = document.querySelector<HTMLElement>(`${CARD_SELECTOR}[data-youtube-job-id="${escapeAttr(job.id)}"]`);
    if (byJobId) return byJobId;
    const candidates = Array.from(document.querySelectorAll<HTMLElement>(`${CARD_SELECTOR}[data-youtube-source-url="${escapeAttr(job.sourceUrl)}"]`));
    return candidates.find((candidate) => {
      const sessionId = candidate.getAttribute("data-session-id");
      const jobId = candidate.getAttribute("data-youtube-job-id");
      return sessionId === job.sessionId && (!jobId || jobId === job.id);
    }) || null;
  }

  function applyJobToCard(card: HTMLElement, job: YoutubeDigestJobView): void {
    rememberJob(job);
    persistJobSnapshot(card, job);
    if (job.status === "done" || job.status === "done_with_warning") {
      setDurableState(card, "checking", durableRevision(card), null);
      syncCardPresentation(card, job);
      void refreshDigestStatus(card);
      return;
    }
    if (job.status === "failed" || job.status === "cancelled") {
      const copy = job.error && ERROR_COPY[job.error]
        ? ERROR_COPY[job.error]
        : STATUS_COPY[job.status] || STATUS_COPY.failed;
      setDurableState(card, durableState(card), durableRevision(card), copy);
    }
    syncCardPresentation(card, job);
  }

  async function jsonRequest(url: string, init?: RequestInit): Promise<FetchResponseLike> {
    return fetch(url, init);
  }

  function applyStatusResult(card: HTMLElement, body: Record<string, unknown>, copyOverride?: string | null): void {
    const extracted = body.extracted === true;
    const revision = typeof body.revision === "string" && body.revision.trim()
      ? body.revision.trim()
      : null;
    setDurableState(card, extracted ? "extracted" : "not_extracted", revision, copyOverride || null);
    syncCardPresentation(card);
  }

  function isFreshStatusRequest(card: HTMLElement, token: string, sessionId: string, sourceUrl: string): boolean {
    return card.getAttribute("data-youtube-status-token") === token
      && (card.getAttribute("data-session-id") || "") === sessionId
      && (card.getAttribute("data-youtube-source-url") || "") === sourceUrl;
  }

  async function refreshDigestStatus(card: HTMLElement): Promise<void> {
    const sessionId = card.getAttribute("data-session-id") || currentSessionId();
    const sourceUrl = card.getAttribute("data-youtube-source-url") || "";
    if (!sessionId || !sourceUrl) {
      setDurableState(card, "check_failed", null, "세션을 확인할 수 없어 기존 digest 상태를 조회하지 못했어요.");
      syncCardPresentation(card);
      return;
    }
    const token = String(Number(card.getAttribute("data-youtube-status-token") || "0") + 1);
    card.setAttribute("data-youtube-status-token", token);
    setDurableState(card, "checking", durableRevision(card), null);
    const transient = jobState(card);
    if (!(transient === "awaiting_overwrite" || (transient && ACTIVE_STATUSES.has(transient)))) {
      syncCardPresentation(card);
    }
    try {
      const response = await jsonRequest(`/api/youtube-digest/status?sessionId=${encodeURIComponent(sessionId)}&url=${encodeURIComponent(sourceUrl)}`);
      const body = await response.json();
      if (!isFreshStatusRequest(card, token, sessionId, sourceUrl)) return;
      if (!response.ok || !isRecord(body) || typeof body.extracted !== "boolean") {
        const errorCode = isRecord(body) && typeof body.error === "string" ? body.error : "";
        setDurableState(card, "check_failed", null, ERROR_COPY[errorCode] || STATUS_COPY.check_failed);
        syncCardPresentation(card);
        return;
      }
      applyStatusResult(card, body);
    } catch {
      if (!isFreshStatusRequest(card, token, sessionId, sourceUrl)) return;
      setDurableState(card, "check_failed", null, STATUS_COPY.check_failed);
      syncCardPresentation(card);
    }
  }

  async function startJob(card: HTMLElement, options: { reextractConfirmed?: boolean } = {}): Promise<void> {
    const sessionId = card.getAttribute("data-session-id") || currentSessionId();
    const sourceUrl = card.getAttribute("data-youtube-source-url") || "";
    if (!sessionId || !sourceUrl) {
      setCardState(card, "disconnected", "세션이 없어 digest를 시작할 수 없어요.");
      renderMeta(card, null);
      renderActions(card);
      return;
    }
    if (options.reextractConfirmed && !durableRevision(card)) {
      await refreshDigestStatus(card);
      return;
    }
    card.setAttribute("data-youtube-pending-action", "시작 중...");
    renderActions(card);
    try {
      const payload: Record<string, unknown> = { sessionId, url: sourceUrl };
      if (options.reextractConfirmed) {
        payload.reextractConfirmed = true;
        payload.revision = durableRevision(card);
      }
      const response = await jsonRequest("/api/youtube-digest/jobs", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await response.json();
      if (response.status === 409 && isRecord(body) && body.error === "youtube_digest_reextract_confirmation_required") {
        applyStatusResult(card, body, ERROR_COPY.youtube_digest_reextract_confirmation_required);
        return;
      }
      if (!response.ok || !isRecord(body) || !isJobView(body.job)) {
        const errorCode = isRecord(body) && typeof body.error === "string" ? body.error : "youtube_digest_runtime_unavailable";
        resetCardToState(card, "failed", ERROR_COPY[errorCode] || STATUS_COPY.failed);
        renderMeta(card, {
          id: card.getAttribute("data-youtube-job-id") || "pending",
          sessionId,
          sourceUrl,
          videoId: parseYoutubeVideoId(sourceUrl) || "",
          status: "failed",
          error: errorCode,
        });
        return;
      }
      applyJobToCard(card, body.job);
    } catch {
      resetCardToState(card, "disconnected", STATUS_COPY.disconnected);
    }
  }

  async function cancelJob(card: HTMLElement): Promise<void> {
    const jobId = card.getAttribute("data-youtube-job-id");
    const sessionId = card.getAttribute("data-session-id") || currentSessionId();
    if (!jobId || !sessionId) return;
    card.setAttribute("data-youtube-pending-action", "취소 중...");
    renderActions(card);
    try {
      const response = await jsonRequest(`/api/youtube-digest/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sessionId }),
      });
      const body = await response.json();
      if (!response.ok) {
        const code = isRecord(body) && typeof body.error === "string" ? body.error : "youtube_digest_job_gone";
        resetCardToState(card, code === "youtube_digest_job_gone" ? "job_lost" : "failed", ERROR_COPY[code] || STATUS_COPY.failed);
        return;
      }
      if (isRecord(body) && isJobView(body.job)) {
        applyJobToCard(card, body.job);
        return;
      }
    } catch {
      resetCardToState(card, "disconnected", STATUS_COPY.disconnected);
    }
  }

  async function overwriteJob(card: HTMLElement): Promise<void> {
    const jobId = card.getAttribute("data-youtube-job-id");
    const sessionId = card.getAttribute("data-session-id") || currentSessionId();
    if (!jobId || !sessionId) return;
    card.setAttribute("data-youtube-pending-action", "덮어쓰는 중...");
    renderActions(card);
    try {
      const response = await jsonRequest(`/api/youtube-digest/jobs/${encodeURIComponent(jobId)}/overwrite`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sessionId }),
      });
      const body = await response.json();
      if (!response.ok) {
        const code = isRecord(body) && typeof body.error === "string" ? body.error : "youtube_digest_overwrite_not_pending";
        resetCardToState(card, code === "youtube_digest_job_gone" ? "job_lost" : "failed", ERROR_COPY[code] || STATUS_COPY.failed);
        return;
      }
      if (isRecord(body) && isJobView(body.job)) {
        applyJobToCard(card, body.job);
        return;
      }
    } catch {
      resetCardToState(card, "disconnected", STATUS_COPY.disconnected);
    }
  }

  function confirmReextract(card: HTMLElement): void {
    if (typeof showConfirmModal !== "function") {
      void startJob(card, { reextractConfirmed: true });
      return;
    }
    showConfirmModal({
      title: "기존 YouTube digest를 재추출할까요?",
      body: "새로 추출하면 자막 수집과 digest 생성 비용이 다시 발생하고, 현재 Inbox 노트를 최신 digest로 덮어씁니다.",
      warning: "확인 시점 이후 기존 노트가 바뀌면 다시 한 번 확인해야 해요.",
      cancelLabel: "아니오",
      confirmLabel: "예, 재추출",
      onConfirm: () => startJob(card, { reextractConfirmed: true }),
    });
  }

  function confirmOverwrite(card: HTMLElement): void {
    if (typeof showConfirmModal !== "function") {
      void overwriteJob(card);
      return;
    }
    showConfirmModal({
      title: "기존 YouTube digest 노트를 덮어쓸까요?",
      body: "현재 Inbox 노트가 새 digest 내용으로 바뀝니다.",
      warning: "기존 노트 본문은 자동으로 복구되지 않아요.",
      cancelLabel: "취소",
      confirmLabel: "덮어쓰기",
      onConfirm: () => overwriteJob(card),
    });
  }

  function decorateMessage(message: HTMLElement | null, text: string, options: YoutubeDigestCardDecorateOptions = {}): boolean {
    if (!message) return false;
    const url = firstYoutubeUrl(text);
    if (!url) return false;
    const content = resolveContentHost(message);
    if (!content) return false;
    const existing = content.querySelector<HTMLElement>(CARD_SELECTOR);
    if (existing) {
      if (options.sessionId && !existing.getAttribute("data-session-id")) {
        existing.setAttribute("data-session-id", options.sessionId);
      }
      void refreshDigestStatus(existing);
      return true;
    }
    const sessionId = options.sessionId || message.dataset.sessionId || currentSessionId();
    const card = buildCard(url, sessionId);
    const time = content.querySelector(".message-time");
    content.insertBefore(card, time || null);
    // 원본 메시지가 restore 뒤에(load-more 등) 렌더되면 진행 중 job 상태를 이어받는다. 끝난 job은
    // durable status 조회만으로 충분하므로 이어받지 않는다.
    // 같은 URL이 세션에 여러 번 있으면 이미 그 job을 들고 있는 카드가 계속 소유한다.
    const pendingJob = sessionId ? pendingJobForCard(sessionId, url) : null;
    if (pendingJob && !document.querySelector(`${CARD_SELECTOR}[data-youtube-job-id="${escapeAttr(pendingJob.id)}"]`)) {
      applyJobToCard(card, pendingJob);
    }
    void refreshDigestStatus(card);
    return true;
  }

  function onJobUpdate(data: YoutubeDigestWsPayload): void {
    const job = data?.job;
    if (!isJobView(job)) return;
    rememberJob(job);
    // 원본 메시지가 DOM에 없으면 아무것도 만들지 않는다(원본이 렌더될 때 decorateMessage가 이어받는다).
    const card = matchCardForJob(job);
    if (!card) return;
    applyJobToCard(card, job);
  }

  function markMissingSessionJobs(sessionId: string, visibleIds: Set<string>): void {
    document.querySelectorAll<HTMLElement>(`${CARD_SELECTOR}[data-session-id="${escapeAttr(sessionId)}"][data-youtube-job-id]`).forEach((card) => {
      const jobId = card.getAttribute("data-youtube-job-id");
      const transient = jobState(card);
      if (jobId && !visibleIds.has(jobId) && (transient === "awaiting_overwrite" || (transient && ACTIVE_STATUSES.has(transient)))) {
        resetCardToState(card, "job_lost", STATUS_COPY.job_lost);
      }
    });
  }

  function sessionCards(sessionId: string): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>(`${CARD_SELECTOR}[data-session-id="${escapeAttr(sessionId)}"]`));
  }

  async function restoreForSession(sessionId: string): Promise<void> {
    if (!sessionId) return;
    try {
      const response = await jsonRequest(`/api/youtube-digest/jobs?sessionId=${encodeURIComponent(sessionId)}`);
      const body = await response.json();
      if (!response.ok || !isRecord(body) || !Array.isArray(body.jobs)) {
        knownJobsBySession.delete(sessionId);
        markMissingSessionJobs(sessionId, new Set());
        return;
      }
      if (currentSessionId() && currentSessionId() !== sessionId) return;
      const jobs = body.jobs.filter(isJobView);
      replaceSessionJobs(sessionId, jobs);
      const visibleIds = new Set(jobs.map((job) => job.id));
      const refreshedCards = new Set<HTMLElement>();
      jobs.forEach((job) => {
        const card = matchCardForJob(job);
        if (card) {
          refreshedCards.add(card);
          applyJobToCard(card, job);
        }
      });
      markMissingSessionJobs(sessionId, visibleIds);
      sessionCards(sessionId).forEach((card) => {
        if (!refreshedCards.has(card)) {
          void refreshDigestStatus(card);
        }
      });
    } catch {
      knownJobsBySession.delete(sessionId);
      sessionCards(sessionId).forEach((card) => {
        resetCardToState(card, "disconnected", STATUS_COPY.disconnected);
      });
    }
  }

  return {
    decorateMessage,
    onJobUpdate,
    restoreForSession,
  };
})();
